import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Button,
  ButtonModel,
  DrawerContext,
  IconSolidInfo,
  Text,
} from '@notino/react-styleguide';

import { PortalTooltip } from '@components/PortalTooltip';
import { IReviewSummary } from '@containers/ProductDetailContainer/model';
import { css } from '@linaria/core';
import { desktopVisibleMd, mobileVisibleMd } from '@styles/common';
import { useIsDesktop } from '@utils/helpers';

import messages from '../../../../../messages';
import { messages as reviewMessage } from '../../messages';
import { RatingsPlural } from '../../RatingsPlural';

import { ratingTooltipMessages } from './messages';
import {
  CollectReviewsLink,
  Header,
  InfoIconButton,
  OverallValue,
  RatingInfoDrawerContent,
  StyledRatings,
} from './styled';

interface IHeaderProps {
  hasReviews: boolean;
  isAllReviewsPage?: boolean;
  reviewSummary: IReviewSummary;
  userHasWrittenReview: boolean;
  productReviewCount: number;
  onWriteMessageClick: () => void;
}

export const ReviewHeader = ({
  hasReviews,
  reviewSummary,
  onWriteMessageClick,
  userHasWrittenReview,
  productReviewCount,
}: IHeaderProps) => {
  const { formatMessage } = useIntl();
  const isDesktop = useIsDesktop();
  const { openDrawer } = DrawerContext.useDrawerContext();

  const link = (
    <CollectReviewsLink
      href={formatMessage(reviewMessage.collectingReviewsLink)}
    >
      {formatMessage(reviewMessage.collectingReviewsText)}
    </CollectReviewsLink>
  );

  return (
    <div>
      <div className={mobileVisibleMd}>{link}</div>
      <Header data-cypress="reviews-averageRating">
        <OverallValue>
          {reviewSummary.averageRating.toFixed(1).replace('.', ',')}
        </OverallValue>

        <StyledRatings
          ratingId="overall"
          rating={hasReviews ? reviewSummary.averageRating : 0}
          size={20}
          background="icon.disabled"
          foreground="icon.primary"
        />

        <Text
          color="text.secondary"
          typography="labelSmall400"
          className={css`
            display: flex;
            align-items: center;
          `}
        >
          ({productReviewCount}{' '}
          <RatingsPlural productRatingCount={productReviewCount} />)
          <PortalTooltip
            disabled={!isDesktop}
            content={
              <PortalTooltip.Content style={{ width: 300 }}>
                <FormattedMessage {...ratingTooltipMessages.text} />
              </PortalTooltip.Content>
            }
          >
            <InfoIconButton
              as="span"
              tabIndex={0}
              onClick={() => {
                if (!isDesktop) {
                  openDrawer(
                    <RatingInfoDrawerContent>
                      <FormattedMessage {...ratingTooltipMessages.text} />
                    </RatingInfoDrawerContent>,
                    {
                      title: (
                        <>
                          <div style={{ width: 44 }} />
                          <Text typography="labelLarge">
                            <FormattedMessage
                              {...ratingTooltipMessages.drawerTitle}
                            />
                          </Text>
                        </>
                      ),
                      initialPosition: 'dynamic',
                    }
                  );
                }
              }}
            >
              <IconSolidInfo
                width="16px"
                height="16px"
                color="icon.tertiary"
                style={{ marginLeft: 4 }}
              />
            </InfoIconButton>
          </PortalTooltip>
        </Text>

        <div className={desktopVisibleMd}>
          <Button
            data-cypress="reviews-addNewReview"
            buttonStyle={ButtonModel.Styles.secondary}
            onClick={onWriteMessageClick}
          >
            <FormattedMessage
              {...(userHasWrittenReview
                ? messages.editReview
                : messages.writeReview)}
            />
          </Button>

          {link}
        </div>
      </Header>
    </div>
  );
};
